import React from 'react';
import { getIntl } from '../../../utils/intl';
import { useProfilesTabNavigation } from '../../../utils/appNavigationNames';
import { PermissionDomainCheck } from '../../v2BaseComponents/Permissions/PermissionCheck';
import CollaboratorAndInviteList from './CollaboratorAndInviteList';
import { useHasPermission, useIsProfileOwner } from '../../v2BaseComponents/Permissions/hooks';
import { ActionButtonLayout } from '../../v2BaseComponents/ActionButtonLayout';
import MainContentContainer from '../../v2BaseComponents/MainContentContainer';
import { ContentStack } from '../../v2BaseComponents/Stack';
import * as Card from '../../v2BaseComponents/Card';
import useMedicalProfileOwner from '../../../hooks/api/useMedicalProfileOwner';
import urls from '../../../utils/urls';
import getName from '../../../utils/getName';
import { AnimateLoadedContent } from '../../v2BaseComponents/Loader';
import { useCollaborators } from '../../../hooks/api/useCollaborators';
import { APITypes } from '../../../primary-record-types';
import useProfileTransferRequest from '../../../hooks/api/useProfileTransferRequest';
import { useRemoveSelfAsCollaboratorButton } from '../../../hooks/useRemoveSelfAsCollaboratorButton';
import { View } from 'react-native';

const intl = getIntl('profile-manage-tab', {
    invite: 'Invite Collaborator',
    access: 'Shared Access',
    owner: 'Profile Organizer',
    addTransferRequest: 'Transfer Owner',
    removeSelfAsCollaborator: 'Remove Self As Collaborator',
    transferRequestDetails: 'Transfer Request Details',
});

export type ProfileAccessContentOwnProps = {
    medicalProfileId: string;
};

export type ProfileAccessContentProps = ProfileAccessContentOwnProps;

const ProfileAccessContent: React.FC<ProfileAccessContentProps> = (props) => {
    const navigation = useProfilesTabNavigation();
    const isProfileOwner = useIsProfileOwner(props.medicalProfileId);
    const hasCollaboratorEdit = useHasPermission(props.medicalProfileId, 'collaborators', 'edit');
    const { isLoadingProfileTransferRequest, profileTransferRequest } = useProfileTransferRequest({
        profileId: props.medicalProfileId,
    });
    const removeSelfAsCollaboratorButton = useRemoveSelfAsCollaboratorButton({
        profileId: props.medicalProfileId,
        onRemoveSuccess: () => {
            navigation.navigate('root');
        },
    });
    const { owner, isLoading } = useMedicalProfileOwner({ medicalProfileId: props.medicalProfileId });
    const { collaboratorInvites, isLoadingCollaboratorInvites, collaborators, isLoadingCollaborators } =
        useCollaborators({
            medicalProfileId: props.medicalProfileId,
        });

    const getAvatar = (owner: APITypes.medicalProfileService.GetMedicalProfileOwnerResponse) => (
        <Card.CardLoadableAvatar
            size="md"
            avatarUrl={urls.api.medicalProfiles.owner.avatar(props.medicalProfileId)}
            firstName={owner.firstName}
            lastName={owner.lastName}
        />
    );

    return (
        <AnimateLoadedContent isLoading={isLoading || isLoadingCollaboratorInvites || isLoadingCollaborators}>
            {() => (
                <>
                    <ActionButtonLayout
                        scrollable
                        actions={[
                            {
                                title: intl('invite'),
                                icon: 'PlusCircle',
                                show: hasCollaboratorEdit,
                                onPress: () => {
                                    navigation.navigate('invite-collaborator', {
                                        profileId: props.medicalProfileId,
                                    });
                                },
                                testID: 'add-collaborator-button',
                            },
                            {
                                title: intl('addTransferRequest'),
                                icon: 'HorizontalArrows2',
                                show: isProfileOwner,
                                onPress: () => {
                                    navigation.navigate('add-transfer-request', {
                                        profileId: props.medicalProfileId,
                                    });
                                },
                                testID: 'create-transfer-request-button',
                            },
                            {
                                icon: 'HorizontalArrows2',
                                title: intl('transferRequestDetails'),
                                onPress: () => {
                                    navigation.navigate('transfer-request', {
                                        profileId: props.medicalProfileId,
                                    });
                                },
                                show:
                                    !isLoadingProfileTransferRequest &&
                                    isProfileOwner &&
                                    (profileTransferRequest.status === 'pending-collaborator-transfer-request' ||
                                        profileTransferRequest.status === 'pending-non-collaborator-transfer-request'),
                                testID: 'transfer-request-details-button',
                            },
                            {
                                icon: 'UserMinus',
                                title: intl('removeSelfAsCollaborator'),
                                onPress: removeSelfAsCollaboratorButton.onPress,
                                show: !isProfileOwner && !!owner,
                                isDisabled: removeSelfAsCollaboratorButton.isRemovingSelfAsCollaborator,
                                testID: 'remove-self-as-collaborator-prompt-button',
                            },
                        ]}
                    >
                        <MainContentContainer>
                            {!owner ? <View testID="no-owner" /> : null}
                            <ContentStack>
                                {owner ? (
                                    <Card.Card testID="owner-card">
                                        <Card.CardLeftContent leftContent={owner ? getAvatar(owner) : null}>
                                            <Card.CardContent>
                                                <Card.CardHeader>{owner ? getName(owner) : ''}</Card.CardHeader>
                                                <Card.CardBody>{intl('owner')}</Card.CardBody>
                                            </Card.CardContent>
                                        </Card.CardLeftContent>
                                    </Card.Card>
                                ) : null}
                                <PermissionDomainCheck
                                    domain="careTeam"
                                    permission={'view'}
                                    medicalProfileId={props.medicalProfileId}
                                    renderWithAccess={() => (
                                        <CollaboratorAndInviteList
                                            collaboratorInvites={collaboratorInvites}
                                            collaborators={collaborators}
                                            profileId={props.medicalProfileId}
                                        />
                                    )}
                                />
                            </ContentStack>
                        </MainContentContainer>
                    </ActionButtonLayout>
                    {removeSelfAsCollaboratorButton.modal}
                </>
            )}
        </AnimateLoadedContent>
    );
};

export default React.memo(ProfileAccessContent);
